import { type ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { captureException } from '@sentry/react-router';

/**
 * Per-component fault boundary for dynamically rendered page-template components.
 * A throwing component is isolated — it renders nothing and reports to Sentry,
 * leaving the rest of the page interactive instead of unmounting the route.
 *
 * Note: React error boundaries only catch client-side render and hydration
 * errors. Errors thrown during the server (streaming) render are not caught
 * here — without a <Suspense> boundary above, they surface via entry.server's
 * onError/onShellError, not this fallback.
 */
export const ComponentErrorBoundary = ({
  children,
  componentName,
}: {
  children: ReactNode;
  componentName?: string;
}) => (
  <ErrorBoundary
    fallbackRender={() => null}
    onError={error => captureException(error, { tags: { component: componentName ?? 'unknown' } })}
  >
    {children}
  </ErrorBoundary>
);
